import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { Theme, useMediaQuery } from '@mui/material';
import { getThemeByMode } from '@/theme';
import { ThemeMode } from '@/types/Settings';
import { useSettingsStore } from '@/stores/settingsStore';

interface ThemeContextValue {
  /** Current active theme object */
  theme: Theme;
  /** Current theme mode preference (light, dark, or system) */
  themeMode: ThemeMode;
  /** Actual resolved theme mode (light or dark, never system) */
  resolvedThemeMode: 'light' | 'dark';
  /** Whether the user prefers dark mode according to system settings */
  prefersDarkMode: boolean;
  /** Function to change theme mode */
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  /** Optional default theme mode (defaults to 'system') */
  defaultMode?: ThemeMode;
}

export function ThemeProvider({ children, defaultMode = 'system' }: ThemeProviderProps) {
  const { settings, updateSettings } = useSettingsStore();

  // Detect system preference for dark mode
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)', {
    noSsr: true, // Prevent hydration mismatch
  });

  // Get theme mode from settings or use default
  const themeMode: ThemeMode = settings.themeMode || defaultMode;

  // Resolve the actual theme mode (convert 'system' to 'light' or 'dark')
  const resolvedThemeMode: 'light' | 'dark' = useMemo(() => {
    if (themeMode === 'system') {
      return prefersDarkMode ? 'dark' : 'light';
    }
    return themeMode;
  }, [themeMode, prefersDarkMode]);

  // Get the actual theme object
  const theme = useMemo(() => {
    return getThemeByMode(resolvedThemeMode);
  }, [resolvedThemeMode]);

  // Function to update theme mode
  const setThemeMode = (mode: ThemeMode) => {
    updateSettings({ themeMode: mode });
  };

  // Provide smooth transition classes to body
  useEffect(() => {
    const body = document.body;

    // Add transition class for smooth theme switching
    body.style.transition = 'background-color 0.3s ease, color 0.3s ease';

    // Add theme mode class for any custom CSS that might need it
    body.classList.remove('theme-light', 'theme-dark');
    body.classList.add(`theme-${resolvedThemeMode}`);

    return () => {
      body.style.transition = '';
      body.classList.remove('theme-light', 'theme-dark');
    };
  }, [resolvedThemeMode]);

  const contextValue: ThemeContextValue = {
    theme,
    themeMode,
    resolvedThemeMode,
    prefersDarkMode,
    setThemeMode,
  };

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
}

/**
 * Hook to access theme context
 * @throws Error if used outside of ThemeProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}
